import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import { ensureAuditSession } from '../features/audit/auditSession';

function buildQueryString(params = {}) {
  const qs = new URLSearchParams();
  if (params.project_id != null && Number.isFinite(Number(params.project_id))) {
    qs.set('project_id', String(params.project_id));
  }
  qs.set('limit', String(params.limit ?? 50));
  qs.set('offset', String(params.offset ?? 0));
  [
    'module',
    'event_type',
    'severity',
    'session_id',
    'actor_id',
    'search',
    'created_from',
    'created_to',
    'entity_type',
    'entity_id',
    'sortBy',
    'sortOrder',
    'source',
  ].forEach((key) => {
    if (params[key]) qs.set(key, String(params[key]));
  });
  return qs.toString();
}

function parseListData(raw, fallbackLimit, fallbackOffset) {
  const data = raw || {};
  const items = Array.isArray(data.audit_trail)
    ? data.audit_trail
    : Array.isArray(data.items)
      ? data.items
      : [];
  return {
    items,
    total: typeof data.total === 'number' ? data.total : items.length,
    limit: typeof data.limit === 'number' ? data.limit : fallbackLimit,
    offset: typeof data.offset === 'number' ? data.offset : fallbackOffset,
  };
}

/** GET /auditTrail/getAuditTrailData */
export async function fetchAuditTrail(params = {}) {
  const { signal, ...queryParams } = params;
  const url = `/auditTrail/getAuditTrailData?${buildQueryString(queryParams)}`;
  try {
    const res = await axiosConfig.get(url, { signal });
    if (res.status === 403) {
      return { ok: false, error: 'Permission denied', statusCode: 403 };
    }
    if (res.status === 200) {
      const payload = get(res, ['data', 'data'], null);
      if (payload == null) {
        return {
          ok: true,
          data: { items: [], total: 0, limit: queryParams.limit ?? 50, offset: queryParams.offset ?? 0 },
        };
      }
      return {
        ok: true,
        data: parseListData(payload, queryParams.limit ?? 50, queryParams.offset ?? 0),
      };
    }
    return { ok: false, error: 'Unexpected response', statusCode: res.status };
  } catch (error) {
    if (signal?.aborted || error?.code === 'ERR_CANCELED') {
      return { ok: false, error: 'Request cancelled' };
    }
    const status = error?.response?.status ?? error?.status;
    if (status === 403) {
      return { ok: false, error: 'Permission denied (admin/super-admin required)', statusCode: 403 };
    }
    if (status === 404 || status === 501) {
      return { ok: false, error: 'Audit trail API is not available yet.', statusCode: status, unavailable: true };
    }
    return {
      ok: false,
      error: error?.response?.data?.message || error?.message || 'Failed to load audit trail',
      statusCode: status,
    };
  }
}

export async function fetchAuditTrailDashboardStats(params = {}) {
  const url = `/auditTrail/getAuditTrailDashboardStats?${buildQueryString(params)}`;
  try {
    const res = await axiosConfig.get(url);
    return { ok: true, data: get(res, ['data', 'data'], {}) };
  } catch (error) {
    return { ok: false, error: error?.message || 'Failed to load stats' };
  }
}

export async function fetchUserActivityTimeline(params = {}) {
  const url = `/auditTrail/getUserActivityTimeline?${buildQueryString(params)}`;
  try {
    const res = await axiosConfig.get(url);
    return { ok: true, data: get(res, ['data', 'data'], {}) };
  } catch (error) {
    return {
      ok: false,
      error: error?.response?.data?.error || error?.message || 'Failed to load timeline',
    };
  }
}

export async function fetchEntityAuditHistory(params = {}) {
  const url = `/auditTrail/getEntityAuditHistory?${buildQueryString(params)}`;
  try {
    const res = await axiosConfig.get(url);
    return { ok: true, data: get(res, ['data', 'data'], {}) };
  } catch (error) {
    return {
      ok: false,
      error: error?.response?.data?.error || error?.message || 'Failed to load entity history',
    };
  }
}

export async function fetchAuditInsights(path, params = {}) {
  const url = `/auditTrail/insights/${path}?${buildQueryString(params)}`;
  try {
    const res = await axiosConfig.get(url);
    return { ok: true, data: get(res, ['data', 'data'], {}) };
  } catch (error) {
    return { ok: false, error: error?.message || 'Failed to load insights' };
  }
}

export async function exportAuditTrailCsv(params = {}) {
  const url = `/auditTrail/exportAuditTrailData?${buildQueryString({ ...params, limit: 5000 })}`;
  const res = await axiosConfig.get(url, { responseType: 'blob' });
  return res.data;
}

export async function logClientAuditEventAsync(params) {
  const sessionId = ensureAuditSession();
  try {
    await axiosConfig.post('/auditTrail/createAuditTrailRecord', {
      ...params,
      sessionId,
      actionDetails: {
        ...(params.actionDetails && typeof params.actionDetails === 'object'
          ? params.actionDetails
          : {}),
        source: params.module === 'ACTIVITY' || String(params.eventType || '').startsWith('ACTIVITY_')
          ? 'UI'
          : params.actionDetails?.source || 'UI',
      },
    });
  } catch (error) {
    console.warn('Failed to write audit trail record', error);
  }
}

export function logClientAuditEvent(params) {
  void logClientAuditEventAsync(params);
}

export function logAccessDeniedAudit(endpoint, method) {
  logClientAuditEvent({
    eventType: 'ACCESS_DENIED',
    module: 'SECURITY',
    entityType: 'API',
    entityId: endpoint || 'unknown',
    severity: 'WARN',
    summary: `Access denied: ${method} ${endpoint}.`,
    displayTitle: endpoint,
    actionDetails: { endpoint, method, status: 403 },
  });
}
